import { motion, useReducedMotion } from "framer-motion";
import { FreshnessChip } from "./FreshnessChip";

/** White pill header — the whole page's visual anchor. Three-column grid
 * so the centered label stays geometrically centered no matter how wide
 * the logo or the chip cluster on the sides grow. Violet/pink aura
 * bleed is painted via a ::before pseudo-element so it extends past the
 * card edge without clipping its rounded corners.
 *
 * Micro-interactions layered on top: the logo "breathes" with a 4s
 * scale loop, an external gradient aura pulses on a 6s cycle, and the
 * liveness dot emits a ring every 2.2s. All motion respects
 * `prefers-reduced-motion`. */
export function Header() {
  const reduced = useReducedMotion();

  return (
    <header className="relative mb-6 isolate">
      {/* Outer violet→pink aura pulsing behind the pill. */}
      <motion.span
        aria-hidden
        className="
          pointer-events-none absolute inset-[-32px] -z-10 rounded-[38px]
          bg-[radial-gradient(55%_70%_at_20%_30%,rgba(167,139,250,0.32)_0%,transparent_65%),radial-gradient(55%_70%_at_80%_70%,rgba(236,72,153,0.26)_0%,transparent_65%)]
          blur-[22px]
        "
        animate={
          reduced
            ? undefined
            : { opacity: [0.75, 1, 0.75], scale: [1, 1.015, 1] }
        }
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <div
        className="
          relative grid w-full items-center gap-4
          rounded-[18px] border border-[rgba(20,30,95,0.08)]
          bg-white px-6 py-4
          shadow-[0_0_48px_-8px_rgba(124,58,237,0.35),0_0_72px_-12px_rgba(236,72,153,0.22),0_14px_36px_-16px_rgba(0,0,0,0.55)]
        "
        style={{ gridTemplateColumns: "1fr auto 1fr" }}
      >
        <div className="flex items-center justify-start">
          <motion.img
            src="/logo-altostratus.svg"
            alt="Altostratus"
            className="h-8 w-auto"
            animate={
              reduced ? undefined : { scale: [1, 1.015, 1] }
            }
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        <div className="flex items-center gap-2.5 justify-self-center">
          <span className="relative inline-flex h-2.5 w-2.5 items-center justify-center">
            {/* Expanding ring — only when motion is allowed. */}
            {!reduced && (
              <motion.span
                aria-hidden
                className="absolute inset-0 rounded-full bg-emerald-500/60"
                animate={{ scale: [1, 2.2], opacity: [0.55, 0] }}
                transition={{
                  duration: 2.2,
                  repeat: Infinity,
                  ease: "easeOut",
                }}
              />
            )}
            {/* Solid core dot with a soft emerald halo. */}
            <motion.span
              aria-hidden
              className="
                brand-dot relative inline-block h-2.5 w-2.5 rounded-full bg-emerald-500
                shadow-[0_0_0_4px_rgba(16,185,129,0.18)]
              "
              animate={
                reduced ? undefined : { scale: [1, 1.12, 1] }
              }
              transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </span>
          <h1 className="text-[1.15rem] font-bold tracking-tight text-[#0f1535]">
            Leaderboard de IA
          </h1>
        </div>

        <div className="flex items-center justify-end">
          <FreshnessChip />
        </div>
      </div>
    </header>
  );
}
